import { useCallback, useEffect, useMemo, useState } from 'react';
import espnowService, {
  type EspNowController,
  type EspNowRouter,
  type RelayZone,
  type ScheduleProgram,
} from '../services/espnowIrrigation';
import {
  buildRelayCatalogue,
  type RelaySlot,
} from '../utils/relayCatalogue';
import type { IrrigationNode } from '../services/irrigationNetwork';

interface Result {
  catalogue: RelaySlot[];
  routersByController: Record<string, EspNowRouter[]>;
  controllers: EspNowController[];
  loading: boolean;
  /** Set only when nothing could be loaded at all — a partial load still lists. */
  error: string | null;
  reload: () => void;
  /**
   * Merge one saved (or deleted) zone into the catalogue without refetching.
   * A zone save is one PUT and the answer is the row itself — going back for
   * every controller's routers and programs to learn what we were just told is
   * a round trip for nothing, and it makes the outline flicker off the map.
   */
  applyZone: (zone: RelayZone | null, routerMac: string, relayIndex: number) => void;
}

/**
 * Every relay the farm's controllers can address, ready to be linked to a node.
 *
 * Assembled client-side from the endpoints that already serve the device pages
 * rather than from a new one, so `usableRelays()` stays the single answer to
 * how many relays a board has. A farm runs one or two controllers, so this is
 * three requests, not a fan-out.
 *
 * A controller whose routers or programs fail to load contributes nothing
 * rather than failing the whole list: a farm with two sites should still be
 * able to link the one that answered.
 */
export function useRelayCatalogue(
  farmId: number | null,
  /** Pass the network's own array, not a fresh one per render — it keys the memo. */
  nodes: IrrigationNode[] | undefined,
): Result {
  const [controllers, setControllers] = useState<EspNowController[]>([]);
  const [routersByController, setRouters] = useState<Record<string, EspNowRouter[]>>({});
  const [programsByController, setPrograms] = useState<Record<string, ScheduleProgram[]>>({});
  const [zonesByController, setZones] = useState<Record<string, RelayZone[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    if (!farmId) {
      setControllers([]); setRouters({}); setPrograms({}); setZones({});
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const all = await espnowService.getControllers();
        const mine = all.filter(c => c.farm_id === farmId);
        const loaded = await Promise.all(mine.map(async (c) => {
          const [routers, programs, zones] = await Promise.all([
            espnowService.getRouters(c.controller_id).catch(() => [] as EspNowRouter[]),
            espnowService.getPrograms(c.controller_id).catch(() => [] as ScheduleProgram[]),
            espnowService.getRelayZones(c.controller_id).catch(() => [] as RelayZone[]),
          ]);
          return { id: c.controller_id, routers, programs, zones };
        }));
        if (cancelled) return;
        const r: Record<string, EspNowRouter[]> = {};
        const p: Record<string, ScheduleProgram[]> = {};
        const z: Record<string, RelayZone[]> = {};
        for (const l of loaded) { r[l.id] = l.routers; p[l.id] = l.programs; z[l.id] = l.zones; }
        setControllers(mine); setRouters(r); setPrograms(p); setZones(z);
      } catch {
        if (!cancelled) {
          setError('Could not load this farm’s controllers');
          setControllers([]); setRouters({}); setPrograms({}); setZones({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [farmId, tick]);

  // Claims come from `nodes`, which the network reload already refreshes — so a
  // link made here re-annotates the list without re-fetching the devices.
  const catalogue = useMemo(
    () => buildRelayCatalogue({
      controllers, routersByController, programsByController, zonesByController,
      nodes: nodes ?? [],
    }),
    [controllers, routersByController, programsByController, zonesByController, nodes],
  );

  const applyZone = useCallback((zone: RelayZone | null, routerMac: string, relayIndex: number) => {
    setZones((prev) => {
      const next: Record<string, RelayZone[]> = {};
      const want = `${routerMac.toUpperCase()}:${relayIndex}`;
      for (const [cid, list] of Object.entries(prev)) {
        // Dropped from every controller before being added back to the one the
        // router is on: a node moved between controllers would otherwise leave
        // its old zone behind, drawn over a relay that no longer exists there.
        next[cid] = list.filter(z => `${z.router_mac.toUpperCase()}:${z.relay_index}` !== want);
      }
      if (zone) {
        const owner = Object.keys(next).find(cid =>
          (routersByController[cid] ?? []).some(r => r.router_mac.toUpperCase() === routerMac.toUpperCase()));
        if (owner) next[owner] = [...next[owner], zone];
      }
      return next;
    });
  }, [routersByController]);

  return { catalogue, routersByController, controllers, loading, error, reload, applyZone };
}

export default useRelayCatalogue;
